import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Search,
  Users,
  User,
  Crown,
  Shield,
  Scissors,
  Building2,
  CreditCard,
  Edit,
  Trash2,
  Eye,
  RefreshCw,
  Filter,
  Download,
  Upload,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Star,
  Clock,
  MoreVertical,
  UserPlus,
  Settings,
  History,
  Zap,
  MapPin,
  ExternalLink
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

/**
 * SuperAdminUserManager - Comprehensive user lookup and management for Super Admin
 */
const SuperAdminUserManager = ({ token }) => {
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({
    role: "all",
    membership: "all",
    status: "all"
  });
  const [recentSearches, setRecentSearches] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Import/Export states
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const [exportCategory, setExportCategory] = useState("all");

  // Export handler
  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        format,
        category: exportCategory
      });
      
      const res = await axios.get(`${API}/admin/export/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (format === "csv") {
        const blob = new Blob([res.data.content], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = res.data.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = res.data.filename.replace('.xlsx', '.json');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
      
      toast.success(`Exported ${res.data.total} users`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  // Import handler
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await axios.post(`${API}/admin/import/users`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      setImportResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Debounced search
  const searchUsers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        q: query,
        role: filters.role,
        membership: filters.membership,
        status: filters.status
      });
      
      const res = await axios.get(`${API}/admin/users/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSearchResults(res.data.users || []);
      
      // Save to recent searches
      if (!recentSearches.includes(query)) {
        setRecentSearches(prev => [query, ...prev.slice(0, 4)]);
      }
    } catch (err) {
      console.error("Search error:", err);
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  }, [token, filters, recentSearches]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        searchUsers(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchUsers]);

  const fetchUserDetails = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/full`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedUser(res.data);
      setEditData(res.data.user || {});
    } catch (err) {
      toast.error("Failed to load user details");
    }
  };

  const handleSaveUser = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${selectedUser.user.user_id}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User updated successfully");
      setEditMode(false);
      fetchUserDetails(selectedUser.user.user_id);
    } catch (err) {
      toast.error("Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSubscription = async (tier, action) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${selectedUser.user.user_id}/subscription`, {
        tier,
        action // 'upgrade', 'downgrade', 'cancel', 'extend'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Subscription ${action}d successfully`);
      fetchUserDetails(selectedUser.user.user_id);
    } catch (err) {
      toast.error(`Failed to ${action} subscription`);
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadge = (user) => {
    const role = user.role || user.user_type;
    const badges = {
      owner: { color: "bg-purple-500", label: "Salon Owner", icon: Building2 },
      stylist: { color: "bg-amber-500", label: "Stylist", icon: Scissors },
      client: { color: "bg-blue-500", label: "Client", icon: User },
      admin: { color: "bg-red-500", label: "Admin", icon: Shield },
      super_admin: { color: "bg-gradient-to-r from-purple-500 to-amber-500", label: "Super Admin", icon: Crown }
    };
    const badge = badges[role] || badges.client;
    return (
      <Badge className={`${badge.color} text-white`}>
        <badge.icon className="w-3 h-3 mr-1" />
        {badge.label}
      </Badge>
    );
  };

  const getMembershipBadge = (tier) => {
    const tiers = {
      free: { color: "bg-zinc-600", label: "Free" },
      pro: { color: "bg-amber-500 text-black", label: "Pro" },
      elite: { color: "bg-purple-500", label: "Elite" }
    };
    const t = tiers[tier] || tiers.free;
    return <Badge className={t.color}>{t.label}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
              <Search className="w-5 h-5 text-white" />
            </div>
            User Lookup & Management
          </h2>
          <p className="text-zinc-400 mt-1">
            Search, view, and edit any user in the system
          </p>
        </div>
        
        {/* Import/Export Buttons */}
        <div className="flex flex-wrap gap-2">
          {/* Import Button */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleImport}
            className="hidden"
            id="admin-import-file"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
            data-testid="admin-import-users-btn"
          >
            {importing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Import Users
          </Button>
          
          {/* Export Dropdown */}
          <div className="flex gap-2">
            <Select value={exportCategory} onValueChange={setExportCategory}>
              <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                <SelectItem value="free">Free Users</SelectItem>
                <SelectItem value="pro">Pro Users</SelectItem>
                <SelectItem value="elite">Elite Users</SelectItem>
                <SelectItem value="stylists">Stylists</SelectItem>
                <SelectItem value="clients">Clients</SelectItem>
              </SelectContent>
            </Select>
            
            <Select onValueChange={handleExport} disabled={exporting}>
              <SelectTrigger 
                className="w-[130px] border-zinc-600 bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500"
                data-testid="admin-export-users-btn"
              >
                <SelectValue placeholder={
                  <span className="flex items-center gap-2">
                    {exporting ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    Export
                  </span>
                } />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">Export as CSV</SelectItem>
                <SelectItem value="excel">Export as Excel</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      
      {/* Import Result */}
      {importResult && (
        <Card className={`${importResult.errors?.length > 0 ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-green-500/30 bg-green-500/5'}`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              {importResult.errors?.length > 0 ? (
                <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5" />
              ) : (
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
              )}
              <div className="flex-1">
                <p className="text-white font-medium">{importResult.message}</p>
                {importResult.errors?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-yellow-400 text-sm mb-1">Warnings:</p>
                    <ul className="text-zinc-400 text-sm space-y-1">
                      {importResult.errors.slice(0, 5).map((err, i) => (
                        <li key={i}>• {err}</li>
                      ))}
                      {importResult.errors.length > 5 && (
                        <li>• ...and {importResult.errors.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setImportResult(null)}
                className="text-zinc-400"
              >
                Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search Section */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-6">
          <div className="space-y-4">
            {/* Main Search */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by email, name, phone, or user ID..."
                className="bg-zinc-800 border-zinc-700 pl-12 pr-4 py-6 text-lg"
                data-testid="user-search-input"
              />
              {loading && (
                <RefreshCw className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-400 animate-spin" />
              )}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              <Select value={filters.role} onValueChange={(v) => setFilters(prev => ({ ...prev, role: v }))}>
                <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="owner">Salon Owner</SelectItem>
                  <SelectItem value="stylist">Stylist</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.membership} onValueChange={(v) => setFilters(prev => ({ ...prev, membership: v }))}>
                <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Membership" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all">All Tiers</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="elite">Elite</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.status} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}>
                <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>

              <Button 
                variant="outline" 
                onClick={() => setFilters({ role: "all", membership: "all", status: "all" })}
                className="border-zinc-700"
              >
                Clear Filters
              </Button>
            </div>

            {/* Recent Searches */}
            {recentSearches.length > 0 && !searchQuery && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-zinc-500">Recent:</span>
                {recentSearches.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => setSearchQuery(q)}
                    className="text-xs px-2 py-1 bg-zinc-800 rounded hover:bg-zinc-700 text-zinc-400"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              <span>Search Results ({searchResults.length})</span>
              <Button variant="outline" size="sm" className="border-zinc-700">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {searchResults.map((user) => (
                <div
                  key={user.user_id || user.email}
                  onClick={() => fetchUserDetails(user.user_id)}
                  className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 cursor-pointer transition-colors"
                  data-testid={`user-result-${user.email}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-zinc-700 rounded-full flex items-center justify-center">
                      {user.profile_image ? (
                        <img src={user.profile_image} alt="" className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <User className="w-6 h-6 text-zinc-400" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium">{user.name || "No Name"}</p>
                        {getRoleBadge(user)}
                        {getMembershipBadge(user.membership_tier || user.membership?.tier)}
                      </div>
                      <p className="text-zinc-400 text-sm">{user.email}</p>
                      {user.phone && (
                        <p className="text-zinc-500 text-xs flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {user.phone}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xs text-zinc-500">
                        Joined {user.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}
                      </p>
                      {user.last_login && (
                        <p className="text-xs text-zinc-500">
                          Last login: {new Date(user.last_login).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <Button variant="ghost" size="sm">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Results */}
      {searchQuery && searchQuery.length >= 2 && !loading && searchResults.length === 0 && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-12 text-center">
            <Search className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-white font-medium mb-2">No users found</h3>
            <p className="text-zinc-400 text-sm">
              Try adjusting your search or filters
            </p>
          </CardContent>
        </Card>
      )}

      {/* User Detail Modal */}
      <Dialog open={!!selectedUser} onOpenChange={() => { setSelectedUser(null); setEditMode(false); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedUser && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-zinc-700 rounded-full flex items-center justify-center">
                      {selectedUser.user?.profile_image ? (
                        <img src={selectedUser.user.profile_image} alt="" className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <User className="w-6 h-6 text-zinc-400" />
                      )}
                    </div>
                    <div>
                      <span>{selectedUser.user?.name || "No Name"}</span>
                      <div className="flex gap-2 mt-1">
                        {getRoleBadge(selectedUser.user || {})}
                        {getMembershipBadge(selectedUser.user?.membership_tier || selectedUser.user?.membership?.tier)}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant={editMode ? "default" : "outline"}
                    onClick={() => setEditMode(!editMode)}
                    className={editMode ? "bg-amber-500 text-black" : "border-zinc-700"}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    {editMode ? "Editing" : "Edit User"}
                  </Button>
                </DialogTitle>
              </DialogHeader>

              <Tabs defaultValue="profile" className="mt-4">
                <TabsList className="bg-zinc-800">
                  <TabsTrigger value="profile">Profile</TabsTrigger>
                  <TabsTrigger value="subscription">Subscription</TabsTrigger>
                  <TabsTrigger value="activity">Activity</TabsTrigger>
                  <TabsTrigger value="notes">Admin Notes</TabsTrigger>
                </TabsList>

                {/* Profile Tab */}
                <TabsContent value="profile" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-400">Email</Label>
                      {editMode ? (
                        <Input
                          value={editData.email || ""}
                          onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                          className="bg-zinc-800 border-zinc-700"
                        />
                      ) : (
                        <p className="text-white">{selectedUser.user?.email}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-zinc-400">Name</Label>
                      {editMode ? (
                        <Input
                          value={editData.name || ""}
                          onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                          className="bg-zinc-800 border-zinc-700"
                        />
                      ) : (
                        <p className="text-white">{selectedUser.user?.name || "Not set"}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-zinc-400">Phone</Label>
                      {editMode ? (
                        <Input
                          value={editData.phone || ""}
                          onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                          className="bg-zinc-800 border-zinc-700"
                        />
                      ) : (
                        <p className="text-white">{selectedUser.user?.phone || "Not set"}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-zinc-400">Role</Label>
                      {editMode ? (
                        <Select 
                          value={editData.role || "client"} 
                          onValueChange={(v) => setEditData(prev => ({ ...prev, role: v }))}
                        >
                          <SelectTrigger className="bg-zinc-800 border-zinc-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            <SelectItem value="client">Client</SelectItem>
                            <SelectItem value="stylist">Stylist</SelectItem>
                            <SelectItem value="owner">Salon Owner</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-white capitalize">{selectedUser.user?.role || "client"}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-zinc-400">User ID</Label>
                      <p className="text-zinc-500 text-sm font-mono">{selectedUser.user?.user_id}</p>
                    </div>
                    <div>
                      <Label className="text-zinc-400">Created</Label>
                      <p className="text-white">
                        {selectedUser.user?.created_at 
                          ? new Date(selectedUser.user.created_at).toLocaleString() 
                          : "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Account Status */}
                  <div className="p-4 bg-zinc-800/50 rounded-lg">
                    <Label className="text-zinc-400 mb-2 block">Account Status</Label>
                    <div className="flex items-center gap-4">
                      {editMode ? (
                        <Select 
                          value={editData.status || "active"} 
                          onValueChange={(v) => setEditData(prev => ({ ...prev, status: v }))}
                        >
                          <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                            <SelectItem value="suspended">Suspended</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge className={
                          selectedUser.user?.status === "suspended" ? "bg-red-500" :
                          selectedUser.user?.status === "inactive" ? "bg-zinc-500" :
                          "bg-green-500"
                        }>
                          {selectedUser.user?.status || "active"}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {editMode && (
                    <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
                      <Button variant="ghost" onClick={() => setEditMode(false)}>
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleSaveUser}
                        disabled={saving}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        {saving ? "Saving..." : "Save Changes"}
                      </Button>
                    </div>
                  )}
                </TabsContent>

                {/* Subscription Tab */}
                <TabsContent value="subscription" className="space-y-4 mt-4">
                  <Card className="bg-zinc-800/50 border-zinc-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center justify-between">
                        <span>Current Subscription</span>
                        {getMembershipBadge(selectedUser.user?.membership_tier || selectedUser.user?.membership?.tier || "free")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-zinc-400">Tier</Label>
                          <p className="text-white capitalize">
                            {selectedUser.user?.membership_tier || selectedUser.user?.membership?.tier || "Free"}
                          </p>
                        </div>
                        <div>
                          <Label className="text-zinc-400">Status</Label>
                          <p className="text-white">
                            {selectedUser.subscription?.status || "Active"}
                          </p>
                        </div>
                        <div>
                          <Label className="text-zinc-400">Started</Label>
                          <p className="text-white">
                            {selectedUser.subscription?.started_at 
                              ? new Date(selectedUser.subscription.started_at).toLocaleDateString()
                              : "N/A"}
                          </p>
                        </div>
                        <div>
                          <Label className="text-zinc-400">Expires</Label>
                          <p className="text-white">
                            {selectedUser.subscription?.expires_at 
                              ? new Date(selectedUser.subscription.expires_at).toLocaleDateString()
                              : "Never"}
                          </p>
                        </div>
                      </div>

                      {/* Subscription Actions */}
                      <div className="pt-4 border-t border-zinc-700">
                        <Label className="text-zinc-400 mb-3 block">Change Subscription</Label>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            onClick={() => handleUpdateSubscription("free", "downgrade")}
                            variant="outline"
                            size="sm"
                            className="border-zinc-600"
                            disabled={saving}
                          >
                            Set to Free
                          </Button>
                          <Button
                            onClick={() => handleUpdateSubscription("pro", "upgrade")}
                            size="sm"
                            className="bg-amber-500 hover:bg-amber-600 text-black"
                            disabled={saving}
                          >
                            <Zap className="w-4 h-4 mr-1" />
                            Upgrade to Pro
                          </Button>
                          <Button
                            onClick={() => handleUpdateSubscription("elite", "upgrade")}
                            size="sm"
                            className="bg-purple-500 hover:bg-purple-600 text-white"
                            disabled={saving}
                          >
                            <Crown className="w-4 h-4 mr-1" />
                            Upgrade to Elite
                          </Button>
                          <Button
                            onClick={() => handleUpdateSubscription(null, "extend")}
                            variant="outline"
                            size="sm"
                            className="border-green-500 text-green-400"
                            disabled={saving}
                          >
                            <Clock className="w-4 h-4 mr-1" />
                            Extend 30 Days
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Payment History */}
                  <Card className="bg-zinc-800/50 border-zinc-700">
                    <CardHeader>
                      <CardTitle className="text-white text-sm">Payment History</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedUser.payments?.length > 0 ? (
                        <div className="space-y-2">
                          {selectedUser.payments.map((payment, i) => (
                            <div key={i} className="flex justify-between items-center p-2 bg-zinc-800 rounded">
                              <div>
                                <p className="text-white text-sm">{payment.description}</p>
                                <p className="text-zinc-500 text-xs">{new Date(payment.date).toLocaleDateString()}</p>
                              </div>
                              <span className="text-green-400">${payment.amount}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-zinc-500 text-sm">No payment history</p>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Activity Tab */}
                <TabsContent value="activity" className="space-y-4 mt-4">
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="bg-zinc-800/50 border-zinc-700">
                      <CardContent className="p-4 text-center">
                        <p className="text-2xl font-bold text-white">
                          {selectedUser.stats?.appointments || 0}
                        </p>
                        <p className="text-zinc-400 text-sm">Total Appointments</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-zinc-800/50 border-zinc-700">
                      <CardContent className="p-4 text-center">
                        <p className="text-2xl font-bold text-green-400">
                          ${selectedUser.stats?.total_spent || 0}
                        </p>
                        <p className="text-zinc-400 text-sm">Total Spent</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-zinc-800/50 border-zinc-700">
                      <CardContent className="p-4 text-center">
                        <p className="text-2xl font-bold text-amber-400">
                          {selectedUser.stats?.reviews || 0}
                        </p>
                        <p className="text-zinc-400 text-sm">Reviews Given</p>
                      </CardContent>
                    </Card>
                  </div>

                  <Card className="bg-zinc-800/50 border-zinc-700">
                    <CardHeader>
                      <CardTitle className="text-white text-sm">Recent Activity</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedUser.activity?.length > 0 ? (
                        <div className="space-y-2">
                          {selectedUser.activity.map((act, i) => (
                            <div key={i} className="flex items-center gap-3 p-2 bg-zinc-800 rounded">
                              <History className="w-4 h-4 text-zinc-500" />
                              <div>
                                <p className="text-white text-sm">{act.action}</p>
                                <p className="text-zinc-500 text-xs">{new Date(act.timestamp).toLocaleString()}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-zinc-500 text-sm">No recent activity</p>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Admin Notes Tab */}
                <TabsContent value="notes" className="space-y-4 mt-4">
                  <Card className="bg-zinc-800/50 border-zinc-700">
                    <CardHeader>
                      <CardTitle className="text-white text-sm">Admin Notes</CardTitle>
                      <CardDescription className="text-zinc-500">
                        Internal notes visible only to admins
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Textarea
                        value={editData.admin_notes || ""}
                        onChange={(e) => setEditData(prev => ({ ...prev, admin_notes: e.target.value }))}
                        placeholder="Add notes about this user..."
                        className="bg-zinc-800 border-zinc-700 min-h-[150px]"
                      />
                      <Button 
                        onClick={handleSaveUser}
                        disabled={saving}
                        className="mt-3 bg-amber-500 hover:bg-amber-600 text-black"
                      >
                        Save Notes
                      </Button>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminUserManager;
